import { createColumnHelper } from "@tanstack/react-table";
import { Badge, Box, Checkbox, Text } from "@chakra-ui/react";
import type { EntityCandidate } from "../../api/models/EntityCandidate";
import {
    CandidateStatus,
    CandidateQualification,
    displayQualification,
    displayEmploymentStatus,
    displayZone,
    DesignatedZoneType,
    EmploymentStatus,
} from "../../api/models/EntityCandidate";

const col = createColumnHelper<EntityCandidate>();

function candidateStatusColor(status: CandidateStatus): string {
    switch (status) {
        case CandidateStatus.Certified:
        case CandidateStatus.Claimed:
            return "green";
        case CandidateStatus.Eligible:
        case CandidateStatus.ReadyForCertification:
        case CandidateStatus.PendingCertification:
            return "blue";
        case CandidateStatus.Denied:
        case CandidateStatus.AppealDenied:
        case CandidateStatus.NotEligible:
        case CandidateStatus.NotHired:
        case CandidateStatus.UnableToSubstantiate:
            return "red";
        case CandidateStatus.PendingDocuments:
        case CandidateStatus.InAppeal:
            return "orange";
        case CandidateStatus.Screening:
        case CandidateStatus.NotScreened:
            return "gray";
        default:
            return "gray";
    }
}

export const candidatesColumns = [
    col.display({
        id: "select",
        header: ({ table }) => (
            <Box onClick={e => e.stopPropagation()}>
                <Checkbox
                    isChecked={table.getIsAllPageRowsSelected()}
                    isIndeterminate={table.getIsSomePageRowsSelected()}
                    onChange={table.getToggleAllPageRowsSelectedHandler()}
                    aria-label="Select all rows on this page"
                />
            </Box>
        ),
        cell: ({ row }) => (
            <Box onClick={e => e.stopPropagation()}>
                <Checkbox isChecked={row.getIsSelected()} onChange={row.getToggleSelectedHandler()} aria-label="Select row" />
            </Box>
        ),
        size: 44,
        enableSorting: false,
        enableColumnFilter: false,
    }),
    col.accessor("lastName", {
        header: "Last Name",
        cell: info => info.getValue() ?? "—",
        enableSorting: true,
        enableColumnFilter: true,
        size: 150,
    }),
    col.accessor("firstName", {
        header: "First Name",
        cell: info => info.getValue() ?? "—",
        enableSorting: true,
        enableColumnFilter: true,
        size: 140,
    }),
    col.accessor("status", {
        header: "Status",
        cell: info => (
            <Badge colorScheme={candidateStatusColor(info.getValue())} fontSize="xs">
                {info.getValue()}
            </Badge>
        ),
        enableSorting: true,
        enableColumnFilter: true,
        size: 170,
    }),
    col.accessor("entityName", {
        header: "Entity",
        cell: info => info.getValue() ?? "—",
        enableSorting: true,
        enableColumnFilter: true,
        size: 180,
    }),
    col.accessor("employmentStatus", {
        header: "Emp. Status",
        cell: info => (info.getValue() ? displayEmploymentStatus(info.getValue() as EmploymentStatus) : "—"),
        enableSorting: true,
        enableColumnFilter: true,
        size: 120,
    }),
    col.accessor("qualifications", {
        header: "Qualifications",
        cell: info => {
            const vals = info.getValue();
            if (!vals || vals.length === 0) return "—";
            return vals.map(q => displayQualification(q as CandidateQualification)).join(", ");
        },
        enableSorting: false,
        enableColumnFilter: false,
        size: 250,
    }),
    col.accessor("designatedZoneType", {
        header: "Zone",
        cell: info => {
            const v = info.getValue();
            return v ? displayZone(v as DesignatedZoneType) : "—";
        },
        enableSorting: false,
        enableColumnFilter: true,
        size: 170,
    }),
    col.accessor("daysRemaining", {
        header: "Days Remaining",
        cell: info => {
            const v = info.getValue();
            if (v == null) return "—";
            return <Text color={v < 30 ? "red.500" : v < 60 ? "orange.400" : undefined}>{v}</Text>;
        },
        enableSorting: true,
        enableColumnFilter: false,
        size: 130,
    }),
    col.accessor("questionnaireCompletedOn", {
        header: "Completed On",
        cell: info => info.getValue() || "—",
        enableSorting: true,
        enableColumnFilter: true,
        size: 140,
    }),
    col.accessor("employmentHireDate", {
        header: "Hire Date",
        cell: info => info.getValue() || "—",
        enableSorting: true,
        enableColumnFilter: true,
        size: 120,
    }),
];
